import { fitFontSize, lineHeightRatioOf, type FitMode } from "./fit_text"

/** Smaller batches in production so long Pretext runs yield between frames (INP). */
const PRODUCTION_BATCH_SIZE = 12
const TEST_BATCH_SIZE = 500

function batchSizeForEnv(): number {
  return typeof process !== "undefined" && process.env.NODE_ENV === "test" ? TEST_BATCH_SIZE : PRODUCTION_BATCH_SIZE
}

function processFitttyElement(containerEl: HTMLElement): void {
  if (containerEl.clientHeight === 0) return

  const innerEl = containerEl.children[0] as HTMLElement
  if (!innerEl) return

  const text = innerEl.textContent || ""
  if (!text) return

  const mode = (containerEl.dataset.fitttyMode as FitMode) || "box"
  const computed = getComputedStyle(innerEl)

  const fontSize = fitFontSize({
    text,
    fontFamily: computed.fontFamily,
    fontWeight: computed.fontWeight,
    containerWidth: containerEl.clientWidth,
    containerHeight: containerEl.clientHeight,
    minFs: Number(containerEl.dataset.fitttyMin) || 20,
    maxFs: Number(containerEl.dataset.fitttyMax) || 52,
    lineHeightRatio: lineHeightRatioOf(computed),
    mode,
  })

  innerEl.style.fontSize = `${fontSize}px`

  containerEl.removeAttribute("data-fittty")
}

/**
 * Fits text inside each [data-fittty] node under `scope` (or the whole document).
 * Work is split across animation frames so taps and typing stay responsive (INP).
 * Resolves when all nodes in the initial snapshot are processed; then dispatches `VanillaFittty`.
 */
const VanillaFittty = (scope?: HTMLElement): Promise<void> => {
  const root = scope ?? document
  const nodes = Array.from(root.querySelectorAll<HTMLElement>("[data-fittty]"))

  if (nodes.length === 0) {
    window.dispatchEvent(new CustomEvent("VanillaFittty", {}))
    return Promise.resolve()
  }

  const batchSize = batchSizeForEnv()

  return new Promise(resolve => {
    let index = 0

    const runBatch = () => {
      const end = Math.min(index + batchSize, nodes.length)
      for (; index < end; index++) {
        processFitttyElement(nodes[index])
      }

      if (index < nodes.length) {
        requestAnimationFrame(runBatch)
      } else {
        window.dispatchEvent(new CustomEvent("VanillaFittty", {}))
        resolve()
      }
    }

    requestAnimationFrame(runBatch)
  })
}

export default VanillaFittty
